import { apiPost } from '../lib/api-utils';

/**
 * A per-contact note (only visible to the user who wrote it).
 * Returned by `get-user-profile` as the `user_notes` field. The server
 * sometimes returns the string `"."` (or an empty body) for a cleared note.
 */
export interface UserNote {
  id: number;
  note: string;
  user_id: number;
  to_user_id: number;
  status?: string;
  created_date?: string;
  updated_date?: string;
}

export interface UserProfileData {
  id: number;
  user_id: number;
  user_name: string;
  user_role_id: number;
  is_document_verified: string;
  two_step_verification: string;
  image: string;
  country_code: string;
  contact_number: string;
  is_email_verified: string;
  time_zone: string;
  dob: string;
  user_status: string;
  email: string;
  last_seen_time: string;
  avg_rating: string;
  no_of_rating: string;
  storage_limit: string;
  current_storage_size: string;
  user_time_zone: string;
  fcm_token: string;
  user_device_id: string;
  phone_no: string;
  server_time_zone: string;
  voip_token: string;
  device_environment: string;
  user_notes: UserNote | string | null;
  user_reward_transaction: unknown[];
  auth_user_id: number;
  to_user_id: string;
}

export interface GetUserProfileRequest {
  user_id: number;
}

export interface GetUserProfileResponse {
  success: boolean;
  code: 200;
  message: "Success";
  data: UserProfileData;
}

export interface UpdateProfileRequest {
  user_id: number;
  user_name?: string;
  dob?: string;
  user_status?: string;
  email?: string;
  phone_no?: string;
  country_code?: string;
  image?: string;
  time_zone?: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  code: number;
  message: string;
  data?: unknown;
}

export interface UploadMediaRequest {
  media: File;
}

export interface UploadMediaResponse {
  success: boolean;
  code: number;
  message: string;
  data?: {
    image_url: string;
    [key: string]: unknown;
  };
}

// Mutation functions for TanStack Query

// Get user profile
export const getUserProfile = async (request: GetUserProfileRequest): Promise<GetUserProfileResponse> => {
  const payload = {
    user_id: request.user_id,
  };
  return apiPost<GetUserProfileResponse>('get-user-profile', payload);
};

// A per-contact personal note (shown only to the user who wrote it).
// Matches iOS `saveEmployeeNotes` and Android `userViewModel.saveUserNote`.
// The server treats `"."` as the cleared state — both native clients send
// `"."` when the user wipes their note.
export interface SaveUserNoteRequest {
  /** User the note is ABOUT. */
  to_user_id: number;
  /** Note text (max 100 chars). Empty string is sent as `"."` per native behaviour. */
  note: string;
  /** Existing note id when updating; omit when creating. */
  id?: number;
}

export interface SaveUserNoteResponse {
  success: boolean;
  code: number;
  message: string;
  data?: {
    id?: number;
    note?: string;
  };
}

export const saveUserNote = async (
  request: SaveUserNoteRequest,
): Promise<SaveUserNoteResponse> => {
  const noteOut = request.note.trim() === '' ? '.' : request.note;
  return apiPost<SaveUserNoteResponse>('save-user-note', {
    to_user_id: request.to_user_id,
    note: noteOut,
    ...(request.id != null ? { id: request.id } : {}),
  });
};

// Update user profile
export const updateProfile = async (request: UpdateProfileRequest): Promise<UpdateProfileResponse> => {
  const payload = {
    user_id: request.user_id,
    ...(request.user_name && { user_name: request.user_name }),
    ...(request.dob && { dob: request.dob }),
    ...(request.user_status && { user_status: request.user_status }),
    ...(request.email && { email: request.email }),
    ...(request.phone_no && { phone_no: request.phone_no }),
    ...(request.country_code && { country_code: request.country_code }),
    ...(request.image && { image: request.image }),
    ...(request.time_zone && { time_zone: request.time_zone }),
  };
  return apiPost<UpdateProfileResponse>('update-profile', payload);
};

// Upload media (profile picture, etc.)
export const uploadMedia = async (request: UploadMediaRequest): Promise<UploadMediaResponse> => {
  const formData = new FormData();
  formData.append('media', request.media);
  
  // For FormData, we need to use a different approach since apiPost expects JSON
  // We'll use the baseFetch directly with FormData
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/upload-media`, {
    method: 'POST',
    body: formData,
    // Don't set Content-Type header - let the browser set it with boundary for FormData
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'Upload request failed');
  }

  return data;
};
